import type { UserOutput } from "@orsane/schema/users";
import { X } from "lucide-react";
import { matchSorter } from "match-sorter";
import * as React from "react";
import { TASK_ROW_HEIGHT } from "@/common/constants";
import { useUsers } from "@/queries/queries";
import { StackedAvatars } from "../StackedAvatars";
import { UserAvatar } from "../UserAvatar";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface UserSelectProps {
    value: UserOutput[];
    onChange: (values: UserOutput[]) => void;
    trigger: (content: React.ReactNode, value: UserOutput[]) => React.ReactElement;
}

export function UserSelect({ value, onChange, trigger }: UserSelectProps) {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const { data } = useUsers({ enabled: open });

    const handleUnselect = (v: UserOutput) => {
        const newValues = value.filter((item) => item.id !== v.id);
        onChange(newValues);
    };

    const handleSelect = (v: string) => {
        const selectedUser = data?.find((u) => u.id === v);
        if (!selectedUser) {
            throw new Error("Selected user not found in list of users");
        }

        const alreadyPresent = value.some((item) => item.id === selectedUser.id);
        if (alreadyPresent) {
            // if already present, remove
            const newValues = value.filter((item) => item.id !== selectedUser.id);
            onChange(newValues);
        } else {
            // if not present, add
            const newValues = [...value, selectedUser];
            onChange(newValues as UserOutput[]);
        }
    };

    const unselectedUsers = data?.filter((u) => !value.some((v) => v.id === u.id)) ?? [];
    const filteredUsers = matchSorter(unselectedUsers, search, { keys: ["name", "email"] });

    return (
        <Popover
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) setSearch("");
            }}
        >
            <PopoverTrigger render={trigger(<StackedAvatars max={2} users={value} size={22} overlap={8} />, value)} />
            <PopoverContent sideOffset={-TASK_ROW_HEIGHT} align="start" className="w-full p-0 max-w-80 gap-0!">
                <div className="p-2 pb-0 flex gap-1 flex-wrap overflow-hidden min-h-[34px]">
                    {!value?.length && (
                        <div className="leading-none grid place-items-center pl-1 text-zinc-400 py-1">
                            No users selected
                        </div>
                    )}
                    {value.map((user) => {
                        return (
                            <div
                                className="flex items-center py-0.5 pl-1 text-sm gap-1 font-normal rounded-sm bg-slate-100 text-sky-950"
                                key={`selected_items_${user.id}`}
                            >
                                <UserAvatar user={user} size="xs" />
                                <div className="text-nowrap py-0.5 ml-1">{user.name}</div>
                                <button
                                    type="button"
                                    className="block pr-1 rounded-r-sm h-full px-0.5 appearance-none text-sky-950 hover:bg-sky-200/70"
                                    onClick={() => handleUnselect(user)}
                                >
                                    <X className="size-3" />
                                </button>
                            </div>
                        );
                    })}
                </div>
                <Command shouldFilter={false}>
                    <CommandInput
                        autoFocus
                        placeholder="Search for a user..."
                        value={search}
                        onValueChange={setSearch}
                    />
                    <CommandList>
                        <CommandGroup>
                            {filteredUsers.map((user) => {
                                return (
                                    <CommandItem
                                        value={user.id.toString()}
                                        key={user.id}
                                        keywords={[user.name, user.email]}
                                        onClick={(e) => e.stopPropagation()}
                                        onSelect={handleSelect}
                                    >
                                        <UserAvatar user={user} size="sm" />
                                        {user.name}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                    <CommandEmpty>No users found.</CommandEmpty>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
